/*
 * Mobile purchase bar. Two contextual states, never both over the real offer:
 *
 *   choose  Shown at the top of the page, before the offer selector has come
 *           into view. "Choose your set" scrolls to the selector. It does NOT
 *           start checkout.
 *   buy     Shown once the real Buy button has scrolled above the viewport.
 *           Shows the exact selection ("2 pillows · Dusk Gray", price) and
 *           starts the same checkout as the real button.
 *
 * Hidden while the offer selector or the real Buy button is on screen, so it
 * never covers what the shopper is choosing. State comes from the offer
 * selector (`nekko:offer-change`, window.nekkoOffer); checkout is requested
 * with `nekko:request-checkout` rather than by clicking the real button.
 */
class StickyProductBar extends HTMLElement {
  connectedCallback() {
    this.offers = document.querySelector('[data-nk-offers]');
    this.buy = document.querySelector('[id^="MainProduct-"] .product-form__submit');
    this.lineEl = this.querySelector('[data-sticky-line]');
    this.priceEl = this.querySelector('[data-sticky-price]');
    this.action = this.querySelector('[data-sticky-action]');
    this.labelEl = this.querySelector('[data-sticky-label]');
    this.errorEl = this.querySelector('[data-sticky-error]');
    // Without an offer selector and a real buy button there is nothing safe to
    // point at, so the bar stays hidden.
    if (!this.offers || !this.buy || !this.action) return;

    this.inView = { offers: false, buy: false };
    this.buyAbove = false;
    this.mode = '';
    this.modeAtPointerDown = null;
    this.state = window.nekkoOffer || null;

    this.onOfferChange = (event) => {
      this.state = event.detail;
      this.render();
    };
    document.addEventListener('nekko:offer-change', this.onOfferChange);

    // Remember what the shopper SAW when they pressed. If the bar changes
    // state under their finger (momentum scroll), the tap is ignored instead
    // of doing the other action.
    this.action.addEventListener('pointerdown', () => { this.modeAtPointerDown = this.mode; });
    this.action.addEventListener('click', () => {
      const seen = this.modeAtPointerDown;
      this.modeAtPointerDown = null;
      if (seen !== null && seen !== this.mode) return;
      this.onAction();
    });

    // The bottom margin stops "in view" counting the strip the bar itself
    // occupies, so the bar hides only when the selector is genuinely visible.
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === this.offers) this.inView.offers = entry.isIntersecting;
        if (entry.target === this.buy) {
          this.inView.buy = entry.isIntersecting;
          this.buyAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        }
      });
      this.render();
    }, { rootMargin: '0px 0px -80px 0px' });
    this.observer.observe(this.offers);
    this.observer.observe(this.buy);

    this.render();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.onOfferChange) document.removeEventListener('nekko:offer-change', this.onOfferChange);
  }

  render() {
    if (!this.action) return;
    const covering = this.inView.offers || this.inView.buy;
    const mode = covering ? '' : (this.buyAbove ? 'buy' : 'choose');
    const show = mode !== '';
    const state = this.state;
    this.mode = mode;

    this.classList.toggle('sticky-product-bar--visible', show);
    this.setAttribute('aria-hidden', show ? 'false' : 'true');
    this.toggleAttribute('inert', !show);

    if (mode === 'buy' && state) {
      this.lineEl.textContent = state.summary;
      this.priceEl.textContent = state.price;
      this.labelEl.textContent = state.busy ? 'Loading checkout…' : (state.purchasable ? 'Buy now' : 'Sold out');
      this.action.disabled = !state.purchasable || state.busy;
    } else {
      this.lineEl.textContent = 'Nekko Align™';
      if (state && state.from) this.priceEl.textContent = 'From ' + state.from;
      this.labelEl.textContent = 'Choose your set';
      this.action.disabled = false;
    }

    const message = mode === 'buy' && state && state.error ? state.error : '';
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  }

  onAction() {
    if (this.mode === 'choose') {
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.offers.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      const selected = this.offers.querySelector('.nk-offer__radio:checked');
      if (selected) selected.focus({ preventScroll: true });
    } else if (this.mode === 'buy') {
      document.dispatchEvent(new CustomEvent('nekko:request-checkout'));
    }
  }
}

if (!customElements.get('sticky-product-bar')) {
  customElements.define('sticky-product-bar', StickyProductBar);
}
